import React from "react";
import {
  Card,
  Row,
  Col,
  Tooltip,
  Typography,
  Collapse,
  Input,
  Select,
} from "antd";
import {
  RobotOutlined,
  ClockCircleOutlined,
  KeyOutlined,
  DollarOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  ChatMessage,
  ChatCompletionRequest,
  ChatCompletionResponse,
} from "../../model_gen/chat";
import { formatCurrency, formatTextOrJson } from "../../utils/FormatUtils";

const { Text } = Typography;
const { Panel } = Collapse;

interface RequestOverviewProps {
  request: ChatCompletionRequest;
  response?: ChatCompletionResponse;
  isEditable?: boolean;
  onMessageEdit?: (index: number, content: string) => void;
  onModelChange?: (model: string) => void;
  modelOptions?: string[];
  style?: React.CSSProperties;
  editingMessageIndex?: number | null;
  onEditingMessageChange?: (index: number | null) => void;
  editMode?: "all" | "final-assistant";
  showDetails?: boolean;
  expanded?: boolean;
}

const RequestOverview: React.FC<RequestOverviewProps> = ({
  request,
  response,
  isEditable = false,
  onMessageEdit,
  onModelChange,
  modelOptions = [],
  style,
  editingMessageIndex,
  onEditingMessageChange,
  editMode = "all",
  showDetails = true,
  expanded = false,
}) => {
  const getMessageIcon = (role: string) => {
    switch (role) {
      case "user":
        return <UserOutlined style={{ marginRight: 8, color: "#52c41a" }} />;
      case "assistant":
        return <RobotOutlined style={{ marginRight: 8, color: "#1890ff" }} />;
      case "system":
        return (
          <InfoCircleOutlined style={{ marginRight: 8, color: "#fa8c16" }} />
        );
      default:
        return null;
    }
  };

  const getMessageStyle = (role: string) => {
    switch (role) {
      case "user":
        return { backgroundColor: "#f6ffed", borderColor: "#b7eb8f" };
      case "assistant":
        return { backgroundColor: "#e6f7ff", borderColor: "#91d5ff" };
      case "system":
        return { backgroundColor: "#fff7e6", borderColor: "#ffd591" };
      default:
        return {};
    }
  };

  const messages = [
    ...(request.params.messages || []),
    ...(response?.choices?.[0]?.message ? [response.choices[0].message] : []),
  ];

  // Get all message indices for defaultActiveKey when expanded
  const defaultActiveKeys = expanded
    ? messages.map((_, index) => index.toString())
    : [messages.length - 1].map(String);

  // Function to determine if a message is editable
  const isMessageEditable = (index: number, message: ChatMessage) => {
    if (!isEditable) return false;

    if (editMode === "final-assistant") {
      return index === messages.length - 1 && message.role === "assistant";
    }

    // In "all" mode, allow editing any message if isEditable is true
    return true;
  };

  const isMessageEdited = (index: number) => {
    const isLastMessage = index === request.params.messages.length - 1;
    return (
      isLastMessage &&
      (request.metadata?._maitai_test_request_response !== undefined ||
        (request.metadata?._maitai_test_request_response_tool_calls &&
          (request.metadata?._maitai_test_request_response_tool_calls as any[])
            .length > 0))
    );
  };

  const renderModelSelect = (value: string, options: string[]) => {
    const groupedModels = groupModelsByProvider(options);
    const sortedProviders = Object.keys(groupedModels).sort((a, b) => {
      if (a.toLowerCase() === "maitai") return -1;
      if (b.toLowerCase() === "maitai") return 1;
      return a.localeCompare(b);
    });

    return (
      <Select
        value={value}
        style={{ width: "100%" }}
        onChange={onModelChange}
        optionLabelProp="label"
        optionFilterProp="children">
        {sortedProviders.map((provider) => (
          <Select.OptGroup
            key={provider}
            label={
              provider.toLowerCase() === "maitai" ? (
                <Typography.Text strong style={{ color: "#21b892" }}>
                  {capitalizeFirstLetter(provider)} (Recommended)
                </Typography.Text>
              ) : (
                capitalizeFirstLetter(provider)
              )
            }>
            {groupedModels[provider].map((model) => {
              const [, ...modelParts] = model.split(":");
              const modelName = modelParts.join(":"); // Rejoin any remaining parts with colons
              return (
                <Select.Option key={model} value={model} label={model}>
                  {modelName}
                </Select.Option>
              );
            })}
          </Select.OptGroup>
        ))}
      </Select>
    );
  };

  const groupModelsByProvider = (models: string[]) => {
    const grouped: { [provider: string]: string[] } = {};
    models.forEach((model) => {
      const [provider] = model.split(":");
      if (!grouped[provider]) {
        grouped[provider] = [];
      }
      grouped[provider].push(model);
    });
    return grouped;
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <Card
      size="small"
      style={{
        background: "#f8f9fa",
        borderRadius: "8px",
        marginBottom: "24px",
        ...style,
      }}>
      {showDetails && (
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Tooltip title="Model used for this request">
              <Card
                size="small"
                style={{ textAlign: "center", height: "100%" }}>
                <RobotOutlined
                  style={{
                    fontSize: "24px",
                    color: "#1890ff",
                    marginBottom: "8px",
                  }}
                />
                <Text type="secondary" style={{ display: "block" }}>
                  Model
                </Text>
                {isEditable && modelOptions.length > 0 ? (
                  renderModelSelect(request.params.model, modelOptions)
                ) : (
                  <Text strong style={{ fontSize: "16px" }}>
                    {request.params.model}
                  </Text>
                )}
              </Card>
            </Tooltip>
          </Col>
          <Col span={6}>
            <Tooltip title="Time taken to process the request">
              <Card
                size="small"
                style={{ textAlign: "center", height: "100%" }}>
                <ClockCircleOutlined
                  style={{
                    fontSize: "24px",
                    color: "#52c41a",
                    marginBottom: "8px",
                  }}
                />
                <Text type="secondary" style={{ display: "block" }}>
                  Time
                </Text>
                <Text strong style={{ fontSize: "16px" }}>
                  {response?.response_time && response.response_time > 0
                    ? `${response.response_time}ms`
                    : "N/A"}
                </Text>
              </Card>
            </Tooltip>
          </Col>
          <Col span={6}>
            <Tooltip title="Number of tokens used">
              <Card
                size="small"
                style={{ textAlign: "center", height: "100%" }}>
                <KeyOutlined
                  style={{
                    fontSize: "24px",
                    color: "#faad14",
                    marginBottom: "8px",
                  }}
                />
                <Text type="secondary" style={{ display: "block" }}>
                  Tokens
                </Text>
                <Text strong style={{ fontSize: "16px" }}>
                  {`${response?.usage?.prompt_tokens || 0} > ${
                    response?.usage?.completion_tokens || 0
                  }`}
                </Text>
              </Card>
            </Tooltip>
          </Col>
          <Col span={6}>
            <Tooltip title="Cost of the request">
              <Card
                size="small"
                style={{ textAlign: "center", height: "100%" }}>
                <DollarOutlined
                  style={{
                    fontSize: "24px",
                    color: "#eb2f96",
                    marginBottom: "8px",
                  }}
                />
                <Text type="secondary" style={{ display: "block" }}>
                  Cost
                </Text>
                <Text strong style={{ fontSize: "16px" }}>
                  {response?.usage?.cost && response.usage.cost > 0
                    ? formatCurrency(response.usage.cost)
                    : "N/A"}
                </Text>
              </Card>
            </Tooltip>
          </Col>
        </Row>
      )}

      <Collapse
        style={{
          marginTop: showDetails ? 20 : 0,
          backgroundColor: "transparent",
        }}
        bordered={false}
        defaultActiveKey={defaultActiveKeys}
        expandIconPosition="end">
        {messages.map((message: ChatMessage, index) => (
          <Panel
            key={index}
            header={
              <div style={{ display: "flex", alignItems: "center" }}>
                {getMessageIcon(message.role)}
                <Text
                  style={{
                    color: "#333",
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}>
                  {message.role}
                  {isMessageEdited(index) && "*"}
                </Text>
              </div>
            }
            style={{
              marginBottom: 16,
              borderRadius: 8,
              border: "1px solid",
              ...getMessageStyle(message.role),
            }}>
            {editingMessageIndex === index &&
            isMessageEditable(index, message) ? (
              <Input.TextArea
                value={message.content}
                onChange={(e) => onMessageEdit?.(index, e.target.value)}
                onBlur={() => onEditingMessageChange?.(null)}
                autoFocus
                autoSize={{ minRows: 2 }}
              />
            ) : (
              <div
                style={{
                  fontSize: 14,
                  whiteSpace: "pre-wrap",
                  padding: "8px 12px",
                  cursor: isMessageEditable(index, message)
                    ? "pointer"
                    : "default",
                }}
                onClick={() =>
                  isMessageEditable(index, message) &&
                  onEditingMessageChange?.(index)
                }>
                {formatTextOrJson(message.content)}
              </div>
            )}
          </Panel>
        ))}
      </Collapse>
    </Card>
  );
};

export default RequestOverview;
